/**
 * LoginPage — container component for WebAuthn-based registration and login.
 *
 * Uses @simplewebauthn/browser to orchestrate the passkey ceremony, then calls
 * the qryptauth backend endpoints at /auth/webauthn/*.
 *
 * Stores the accountId in localStorage after registration so the login form
 * can pre-fill it on the same device.
 */
import { useReducer } from 'react'; // rationale: reducer for login/register flow control and form values
import { useNavigate, useLocation } from 'react-router-dom'; // rationale: post-auth redirect to protected page
import LoginPageView from './LoginPageView'; // rationale: separate view layer per container/view pattern

// Dynamic imports for @simplewebauthn/browser — ESM package consumed by the frontend bundler (Vite)
async function startRegistrationFlow(options: any) {
    const { startRegistration } = await import('@simplewebauthn/browser'); // rationale: browser WebAuthn registration ceremony
    return startRegistration(options);
}
async function startAuthenticationFlow(options: any) {
    const { startAuthentication } = await import('@simplewebauthn/browser'); // rationale: browser WebAuthn authentication ceremony
    return startAuthentication(options);
}

type Mode = 'register' | 'login';
type Status = 'idle' | 'loading' | 'success' | 'error';

interface LoginState {
    mode: Mode; // current UI tab
    accountIdInput: string; // value of the account-ID input in login mode
    status: Status; // current operation status
    errorMessage: string | null; // displayed when status === 'error'
}

type LoginAction =
    | { type: 'SET_MODE'; payload: Mode }
    | { type: 'SET_ACCOUNT_ID'; payload: string }
    | { type: 'SET_LOADING' }
    | { type: 'SET_SUCCESS' }
    | { type: 'SET_ERROR'; payload: string };

const loginReducer = (state: LoginState, action: LoginAction): LoginState => {
    switch (action.type) {
        case 'SET_MODE': return { ...state, mode: action.payload, status: 'idle', errorMessage: null };
        case 'SET_ACCOUNT_ID': return { ...state, accountIdInput: action.payload };
        case 'SET_LOADING': return { ...state, status: 'loading', errorMessage: null };
        case 'SET_SUCCESS': return { ...state, status: 'success', errorMessage: null };
        case 'SET_ERROR': return { ...state, status: 'error', errorMessage: action.payload };
        default: return state;
    }
};

const ACCOUNT_ID_KEY = 'qreditcharge_accountId'; // localStorage key for persisting accountId across sessions

const LoginPage = () => {
    console.log('LoginPage is running');
    const navigate = useNavigate();
    const location = useLocation();
    const from: string = (location.state as any)?.from?.pathname || '/admin'; // redirect target after successful auth

    const savedAccountId = typeof window !== 'undefined' ? window.localStorage.getItem(ACCOUNT_ID_KEY) || '' : '';
    const [state, dispatch] = useReducer(loginReducer, {
        mode: 'register',
        accountIdInput: savedAccountId, // pre-fill if previously registered on this device
        status: 'idle',
        errorMessage: null,
    });

    /**
     * Runs the WebAuthn registration ceremony for a new anonymous account.
     * On success saves accountId to localStorage and redirects to the protected page.
     */
    const handleRegister = async () => {
        console.log('handleRegister is running');
        dispatch({ type: 'SET_LOADING' });
        try {
            const startRes = await fetch('/auth/webauthn/start-registration', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ mode: 'new' }), // request new anonymous account
            });
            if (!startRes.ok) {
                const err = await startRes.json().catch(() => ({}));
                throw new Error(err?.error || `Registration start failed (${startRes.status})`);
            }
            const { accountId, publicKey } = await startRes.json(); // publicKey = WebAuthn registration options

            const credential = await startRegistrationFlow(publicKey); // browser passkey ceremony

            const finishRes = await fetch('/auth/webauthn/finish-registration', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ accountId, credential }), // submit credential to server for verification
            });
            if (!finishRes.ok) {
                const err = await finishRes.json().catch(() => ({}));
                throw new Error(err?.error || `Registration finish failed (${finishRes.status})`);
            }

            window.localStorage.setItem(ACCOUNT_ID_KEY, accountId); // persist for future logins on this device
            dispatch({ type: 'SET_SUCCESS' });
            console.log('handleRegister is returning success');
            navigate(from, { replace: true }); // redirect to the originally requested page
        } catch (err: any) {
            const message = err?.message || 'Registration failed';
            dispatch({ type: 'SET_ERROR', payload: message });
            console.log(`handleRegister is returning error: ${message}`);
        }
    };

    /**
     * Runs the WebAuthn authentication ceremony for an existing account.
     * On success redirects to the protected page.
     */
    const handleLogin = async () => {
        console.log('handleLogin is running');
        const accountId = state.accountIdInput.trim();
        if (!accountId) {
            dispatch({ type: 'SET_ERROR', payload: 'Account ID is required' });
            return;
        }
        dispatch({ type: 'SET_LOADING' });
        try {
            const startRes = await fetch('/auth/webauthn/start-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ accountId }), // initiate challenge for this account
            });
            if (!startRes.ok) {
                const err = await startRes.json().catch(() => ({}));
                throw new Error(err?.error || `Login start failed (${startRes.status})`);
            }
            const { publicKey } = await startRes.json(); // publicKey = WebAuthn authentication options

            const credential = await startAuthenticationFlow(publicKey); // browser passkey ceremony

            const finishRes = await fetch('/auth/webauthn/finish-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ credential }), // submit assertion to server for verification
            });
            if (!finishRes.ok) {
                const err = await finishRes.json().catch(() => ({}));
                throw new Error(err?.error || `Login finish failed (${finishRes.status})`);
            }

            dispatch({ type: 'SET_SUCCESS' });
            console.log('handleLogin is returning success');
            navigate(from, { replace: true }); // redirect to the originally requested page
        } catch (err: any) {
            const message = err?.message || 'Login failed';
            dispatch({ type: 'SET_ERROR', payload: message });
            console.log(`handleLogin is returning error: ${message}`);
        }
    };

    console.log('LoginPage is returning login page view');
    return (
        <LoginPageView
            mode={state.mode}
            accountIdInput={state.accountIdInput}
            status={state.status}
            errorMessage={state.errorMessage}
            onModeChange={(m) => dispatch({ type: 'SET_MODE', payload: m })}
            onAccountIdChange={(v) => dispatch({ type: 'SET_ACCOUNT_ID', payload: v })}
            onRegister={handleRegister}
            onLogin={handleLogin}
        />
    );
};

export default LoginPage;
